import {
  ActionItemPriorities,
  ActionItemStatuses,
  SupportRequestStatuses,
  SupportRequestPriorities,
} from "@/lib/issue/constants"
import type { BadgeProps } from "@/components/ui/badge"
import type { LinearIssue } from "../linear/issue/mod"
import type { Issue, IssueType } from "./mod"
import { Icon } from "@/components/ui/icon"
import { cn } from "@/lib/utils"

type IconProps = {
  className?: string
}
export type StatusConfig = {
  value: string
  label: string
  icon?: (props: IconProps) => React.ReactNode
  badgeVariant: BadgeProps["variant"]
}[]

export const actionItemStatuses: StatusConfig = ActionItemStatuses.map((s) => {
  switch (s) {
    case "Backlog": {
      return {
        value: "Backlog",
        label: "Todo",
        icon: ({ className }: IconProps) => (
          <Icon
            icon="lucide:circle-dashed"
            className={cn("h-[15px] w-[15px] !text-slate-9", className)}
          />
        ),
        badgeVariant: "slate",
      }
    }
    case "Todo": {
      return {
        value: "Todo",
        label: "Todo",
        icon: ({ className }: IconProps) => (
          <Icon
            icon="radix-icons:circle"
            className={cn("h-[15px] w-[15px] !text-slate-9", className)}
          />
        ),
        badgeVariant: "slate",
      }
    }
    case "In Progress": {
      return {
        value: "In Progress",
        label: "In Progress",
        icon: ({ className }: IconProps) => (
          // <ArrowUpCircle className={cn(className, "text-amber-600")} />
          <Icon
            icon="radix-icons:shadow"
            className={cn("h-[15px] w-[15px] !text-amber-9", className)}
          />
          // <ShadowInnerIcon className={cn(className, "text-amber-600")} />
        ),
        badgeVariant: "amber",
      }
    }
    case "Triage": {
      return {
        value: "Triage",
        label: "In Review",
        icon: ({ className }: IconProps) => (
          <Icon
            icon="radix-icons:shadow-inner"
            className={cn("h-[15px] w-[15px] !text-blue-9", className)}
          />
        ),
        badgeVariant: "blue",
      }
    }
    case "In Review": {
      return {
        value: "In Review",
        label: "In Review",
        icon: ({ className }: IconProps) => (
          <Icon
            icon="radix-icons:shadow-inner"
            className={cn("h-[15px] w-[15px] !text-blue-9", className)}
          />
        ),
        badgeVariant: "blue",
      }
    }
    case "Done": {
      return {
        value: "Done",
        label: "Done",
        icon: ({ className }: IconProps) => (
          <Icon
            icon="lucide:check-circle-2"
            className={cn("h-[15px] w-[15px] !text-green-9", className)}
          />
        ),
        badgeVariant: "green",
      }
    }
    case "Completed": {
      return {
        value: "Completed",
        label: "Done",
        icon: ({ className }: IconProps) => (
          <Icon
            icon="lucide:check-circle-2"
            className={cn("h-[15px] w-[15px] !text-green-9", className)}
          />
        ),
        badgeVariant: "green",
      }
    }
    case "Canceled": {
      return {
        value: "Canceled",
        label: "Canceled",
        icon: ({ className }: IconProps) => (
          <Icon
            icon="lucide:x-circle"
            className={cn("h-[15px] w-[15px] !text-slate-9", className)}
          />
        ),
        badgeVariant: "slate",
      }
    }
  }
})

export const supportRequestStatuses: StatusConfig = SupportRequestStatuses.map(
  (s) => {
    switch (s) {
      case "Blocked": {
        return {
          value: "Blocked",
          label: "Blocked",
          icon: ({ className }: IconProps) => (
            <Icon
              icon="radix-icons:shadow-none"
              className={cn("h-[15px] w-[15px] !text-red-9", className)}
            />
          ),
          badgeVariant: "red",
        }
      }
      case "Triage": {
        return {
          value: "Triage",
          label: "Triage",
          icon: ({ className }: IconProps) => (
            <Icon
              icon="radix-icons:shadow-inner"
              className={cn("h-[15px] w-[15px] !text-orange-9", className)}
            />
          ),
          badgeVariant: "orange",
        }
      }
      case "Backlog": {
        return {
          value: "Backlog",
          label: "Accepted",
          icon: ({ className }: IconProps) => (
            <Icon
              icon="lucide:circle-dashed"
              className={cn("h-[15px] w-[15px] !text-blue-9", className)}
            />
          ),
          badgeVariant: "blue",
        }
      }
      case "Todo": {
        return {
          value: "Todo",
          label: "Accepted",
          icon: ({ className }: IconProps) => (
            <Icon
              icon="radix-icons:circle"
              className={cn("h-[15px] w-[15px] !text-blue-9", className)}
            />
          ),
          badgeVariant: "blue",
        }
      }
      case "In Progress": {
        return {
          value: "In Progress",
          label: "In Progress",
          icon: ({ className }: IconProps) => (
            // <ArrowUpCircle className={cn(className, "text-amber-600")} />
            <Icon
              icon="radix-icons:shadow"
              className={cn("h-[15px] w-[15px] !text-amber-9", className)}
            />
            // <ShadowInnerIcon className={cn(className, "text-amber-600")} />
          ),
          badgeVariant: "amber",
        }
      }
      case "In Review": {
        return {
          value: "In Review",
          label: "In Progress",
          icon: ({ className }: IconProps) => (
            <Icon
              icon="radix-icons:shadow-inner"
              className={cn("h-[15px] w-[15px] !text-amber-9", className)}
            />
          ),
          badgeVariant: "amber",
        }
      }
      case "Client Input Required": {
        return {
          value: "Client Input Required",
          label: "Input Required",
          icon: ({ className }: IconProps) => (
            <Icon
              icon="radix-icons:shadow-inner"
              className={cn("h-[15px] w-[15px] !text-plum-9", className)}
            />
          ),
          badgeVariant: "plum",
        }
      }
      case "Client Review": {
        return {
          value: "Client Review",
          label: "For Review",
          icon: ({ className }: IconProps) => (
            <Icon
              icon="radix-icons:shadow-inner"
              className={cn("h-[15px] w-[15px] !text-mint-9", className)}
            />
          ),
          badgeVariant: "mint",
        }
      }
      case "Done": {
        return {
          value: "Done",
          label: "Done",
          icon: ({ className }: IconProps) => (
            <Icon
              icon="lucide:check-circle-2"
              className={cn("h-[15px] w-[15px] !text-green-9", className)}
            />
          ),
          badgeVariant: "green",
        }
      }
      case "Completed": {
        return {
          value: "Completed",
          label: "Done",
          icon: ({ className }: IconProps) => (
            <Icon
              icon="lucide:check-circle-2"
              className={cn("h-[15px] w-[15px] !text-green-9", className)}
            />
          ),
          badgeVariant: "green",
        }
      }
      case "Duplicate": {
        return {
          value: "Duplicate",
          label: "Duplicate",
          icon: ({ className }: IconProps) => (
            <Icon
              icon="lucide:x-circle"
              className={cn("h-[15px] w-[15px] !text-slate-9", className)}
            />
          ),
          badgeVariant: "slate",
        }
      }
      case "Declined": {
        return {
          value: "Declined",
          label: "Declined",
          icon: ({ className }: IconProps) => (
            <Icon
              icon="lucide:x-circle"
              className={cn("h-[15px] w-[15px] !text-slate-9", className)}
            />
          ),
          badgeVariant: "slate",
        }
      }
      case "Canceled": {
        return {
          value: "Canceled",
          label: "Canceled",
          icon: ({ className }: IconProps) => (
            <Icon
              icon="lucide:x-circle"
              className={cn("h-[15px] w-[15px] !text-slate-9", className)}
            />
          ),
          badgeVariant: "slate",
        }
      }
    }
  },
)

export type PriorityConfig = {
  value: string
  label: string
  icon?: (props: IconProps) => React.ReactNode
  badgeVariant: BadgeProps["variant"]
}[]

export const actionItemPriorities: PriorityConfig = ActionItemPriorities.map(
  (p) => {
    switch (p) {
      case "Urgent": {
        return {
          label: "Blocker",
          value: "Urgent",
          icon: ({ className }: IconProps) => (
            <Icon
              icon="lucide:arrow-up-to-line"
              className={cn("h-[15px] w-[15px]", className)}
            />
          ),
          badgeVariant: "destructive",
        }
      }
      case "High": {
        return {
          label: "High",
          value: "High",
          icon: ({ className }: IconProps) => (
            <Icon
              icon="lucide:arrow-up-to-line"
              className={cn("h-[15px] w-[15px] !text-slate-9", className)}
            />
          ),
          badgeVariant: "orange",
        }
      }
      case "Medium": {
        return {
          label: "Medium",
          value: "Medium",
          icon: ({ className }: IconProps) => (
            <Icon
              icon="lucide:arrow-down-to-line"
              className={cn("h-[15px] w-[15px] !text-slate-9", className)}
            />
          ),
          badgeVariant: "blue",
        }
      }
      case "No priority": {
        return {
          label: "No priority",
          value: "No priority",
          icon: ({ className }: IconProps) => (
            <Icon
              icon="lucide:arrow-down-to-line"
              className={cn("h-[15px] w-[15px] !text-slate-9", className)}
            />
          ),
          badgeVariant: "slate",
        }
      }
      case "Normal": {
        return {
          label: "Normal",
          value: "Normal",
          icon: ({ className }: IconProps) => (
            <Icon
              icon="lucide:arrow-down-to-line"
              className={cn("h-[15px] w-[15px] !text-slate-9", className)}
            />
          ),
          badgeVariant: "blue",
        }
      }
      case "Low": {
        return {
          label: "Low",
          value: "Low",
          icon: ({ className }: IconProps) => (
            <Icon
              icon="lucide:arrow-down-to-line"
              className={cn("h-[15px] w-[15px] !text-slate-9", className)}
            />
          ),
          badgeVariant: "slate",
        }
      }
    }
  },
)

export const supportRequestPriorities: PriorityConfig =
  SupportRequestPriorities.map((p) => {
    switch (p) {
      case "Urgent": {
        return {
          label: "Urgent",
          value: "Urgent",
          icon: ({ className }: IconProps) => (
            <Icon
              icon="lucide:arrow-up-to-line"
              className={cn("h-[15px] w-[15px]", className)}
            />
          ),
          badgeVariant: "destructive",
        }
      }
      case "High": {
        return {
          label: "High",
          value: "High",
          icon: ({ className }: IconProps) => (
            <Icon
              icon="lucide:arrow-up-to-line"
              className={cn("h-[15px] w-[15px] !text-slate-9", className)}
            />
          ),
          badgeVariant: "orange",
        }
      }
      case "Medium": {
        return {
          label: "Medium",
          value: "Medium",
          icon: ({ className }: IconProps) => (
            <Icon
              icon="lucide:arrow-down-to-line"
              className={cn("h-[15px] w-[15px] !text-slate-9", className)}
            />
          ),
          badgeVariant: "blue",
        }
      }
      case "No priority": {
        return {
          label: "No priority",
          value: "No priority",
          icon: ({ className }: IconProps) => (
            <Icon
              icon="lucide:arrow-down-to-line"
              className={cn("h-[15px] w-[15px] !text-slate-9", className)}
            />
          ),
          badgeVariant: "slate",
        }
      }
      case "Normal": {
        return {
          label: "Normal",
          value: "Normal",
          icon: ({ className }: IconProps) => (
            <Icon
              icon="lucide:arrow-down-to-line"
              className={cn("h-[15px] w-[15px] !text-slate-9", className)}
            />
          ),
          badgeVariant: "blue",
        }
      }
      case "Low": {
        return {
          label: "Low",
          value: "Low",
          icon: ({ className }: IconProps) => (
            <Icon
              icon="lucide:arrow-down-to-line"
              className={cn("h-[15px] w-[15px] !text-slate-9", className)}
            />
          ),
          badgeVariant: "slate",
        }
      }
    }
  })

export const feedbackItemStatuses = supportRequestStatuses
export const feedbackItemPriorities = supportRequestPriorities

export function getStatusFromLinearIssue(issue: LinearIssue, type: IssueType) {
  switch (type) {
    case "action-item": {
      return actionItemStatuses.find(
        (s) => s.value.toLowerCase() === issue.state.name?.toLowerCase(),
      )
    }
    case "feedback-item": {
      return feedbackItemStatuses.find(
        (s) => s.value.toLowerCase() === issue.state.name?.toLowerCase(),
      )
    }
    case "support-request": {
      return supportRequestStatuses.find(
        (s) => s.value.toLowerCase() === issue.state.name?.toLowerCase(),
      )
    }
  }
}

export function getStatusFromIssue(issue: Issue) {
  switch (issue.type) {
    case "action-item": {
      return actionItemStatuses.find(
        (s) => s.value.toLowerCase() === issue.state.name?.toLowerCase(),
      )
    }
    case "feedback-item": {
      return feedbackItemStatuses.find(
        (s) => s.value.toLowerCase() === issue.state.name?.toLowerCase(),
      )
    }
    case "support-request": {
      return supportRequestStatuses.find(
        (s) => s.value.toLowerCase() === issue.state.name?.toLowerCase(),
      )
    }
  }
}

export function getStatusFromLinearIssueState(
  state: LinearIssue["state"],
  type: IssueType,
) {
  switch (type) {
    case "action-item": {
      return actionItemStatuses.find(
        (s) => s.value.toLowerCase() === state.name?.toLowerCase(),
      )
    }
    case "feedback-item": {
      return feedbackItemStatuses.find(
        (s) => s.value.toLowerCase() === state.name?.toLowerCase(),
      )
    }
    case "support-request": {
      return supportRequestStatuses.find(
        (s) => s.value.toLowerCase() === state.name?.toLowerCase(),
      )
    }
  }
}

export function getPriorityFromLinearIssue(
  issue: LinearIssue,
  type: IssueType,
) {
  switch (type) {
    case "action-item": {
      return actionItemPriorities.find(
        (s) => s.value.toLowerCase() === issue.priorityLabel?.toLowerCase(),
      )
    }
    case "feedback-item": {
      return feedbackItemPriorities.find(
        (s) => s.value.toLowerCase() === issue.priorityLabel?.toLowerCase(),
      )
    }
    case "support-request": {
      return supportRequestPriorities.find(
        (s) => s.value.toLowerCase() === issue.priorityLabel?.toLowerCase(),
      )
    }
  }
}

export function getPriorityFromIssue(issue: Issue) {
  switch (issue.type) {
    case "action-item": {
      return actionItemPriorities.find(
        (s) => s.value.toLowerCase() === issue.priorityLabel?.toLowerCase(),
      )
    }
    case "feedback-item": {
      return feedbackItemPriorities.find(
        (s) => s.value.toLowerCase() === issue.priorityLabel?.toLowerCase(),
      )
    }
    case "support-request": {
      return supportRequestPriorities.find(
        (s) => s.value.toLowerCase() === issue.priorityLabel?.toLowerCase(),
      )
    }
  }
}
